import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import GridConnectionChargeMonitor from '@/components/grid-connection-charge-monitor';
import { gridConnectionDetailsRoute } from '@/modules/charge-center/grid-connections/grid-connection-details/route';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Drawer } from '@mui/material';
import { Outlet, useLocation, useRouter } from '@tanstack/react-router';
import ChargePointDetails from 'components/ChargePointDetails';
import UptimeOnboarding from 'components/ChargePointUptime/UptimeOnboading';
import ConnectorDetails from 'components/ConnectorDetails';
import OnboardingButton from 'components/OnboardingButton';
import SectionTitle from 'components/SectionTitle';
import ViewLoading from 'components/ViewLoading';
import { useAppDispatch, useAppSelector } from 'features';
import {
  fetchChargePointsByGridConnectionId,
  getSelectedChargePointSelector,
  removeChargePointByGridConnectionId,
  removeFetchingChargePoint,
  selectChargePoint,
  selectFetchingChargePoints,
  setSelectedChargePoint,
  unselectChargePoint,
  updateChargePoint,
} from 'features/ChargePoints';
import { showSnackbarMessage } from 'features/Common';
import {
  addDesyncedChargingConnector,
  fetchConnectorsByGridConnectionId,
  fetchUpdatedConnector,
  removeConnectorByGridConnectionId,
  removeConnectorById,
  removeDesyncedChargingConnector,
  selectConnector,
  selectConnectorsByGridConnectionId,
  setSelectedConnector,
  unselectConnector,
} from 'features/Connectors';
import {
  fetchGridConnection,
  fetchGridConnectionChargersPower,
  fetchGridConnectionMetersPower,
  getChargerPowersSelector,
  getGridConnectionByIdSelector,
  getMeterPowersSelector,
  updateGridConnection,
} from 'features/GridConnections';
import { loadSampledValuesByTransactionId } from 'features/Transactions';
import { useModal } from 'hooks/useModal';
import useOnboarding from 'hooks/useOnboarding';
import { X } from 'lucide-react';
import { shallowEqual } from 'react-redux';
import { ConnectorStatusEnum } from 'types/swagger';
import IntlMessages from 'util/IntlMessages';

/**
 * ChargeCenter/Details is the view that displays a single grid connection
 * and its details, regarding chargePoints, connectors and vehicles statuses. This view is the one that makes the fetch
 * request that passes the info to the child components
 *
 * @returns JSX.Element
 */
const Details = () => {
  const { synclogWebsocket } = useContext(UserSessionContext);
  const location = useLocation();
  const navigate = gridConnectionDetailsRoute.useNavigate();
  const route = useRouter();
  const { gridConnectionId } = gridConnectionDetailsRoute.useParams();
  const { connector, chargepoint } = gridConnectionDetailsRoute.useSearch();

  const dispatch = useAppDispatch();
  const firstRender = useRef(true);
  const selectedGridConnection = useAppSelector(
    (state) => getGridConnectionByIdSelector(state, Number(gridConnectionId)),
    shallowEqual
  );
  const selectedChargePoint = useAppSelector(getSelectedChargePointSelector, shallowEqual);
  const selectedConnector = useAppSelector((state) => state.connectors.selectedConnector);

  const chargePoints = useAppSelector(
    (state) => state.chargePoints.byGridConnectionId[Number(gridConnectionId)]
  );
  const fetchingChargePoints = useAppSelector(selectFetchingChargePoints);
  const connectorsByGridConnection = useAppSelector(
    (state) => selectConnectorsByGridConnectionId(state, Number(gridConnectionId)),
    shallowEqual
  );
  const metersPower = useAppSelector(getMeterPowersSelector, shallowEqual);
  const chargersPower = useAppSelector(getChargerPowersSelector, shallowEqual);

  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const { isOpen, closeModal, openModal } = useModal();

  const gridConnectionChargePoints = useMemo(() => {
    if (!chargePoints) return [];
    return Object.values(chargePoints).filter(
      (chargePoint) => chargePoint.grid_connection === Number(gridConnectionId)
    );
  }, [chargePoints, gridConnectionId]);

  const gridConnectionConnectors = useMemo(() => {
    if (!connectorsByGridConnection) return [];
    return Object.values(connectorsByGridConnection);
  }, [connectorsByGridConnection]);

  /**
   * The scope of the tabs migration is to remove the old Tabs component in favor of the new one.
   * This migration does not include changing the structure of the ChargeCenter component nor to migrate to the new layout structure.
   * Therefore, the tabs implementation here is kept as close to the original as possible.
   */

  const currentTab: 'chargers' | 'connectors' = location.pathname.endsWith(
    `/charge-center/grid-connections/${gridConnectionId}/charge-points`
  )
    ? 'chargers'
    : 'connectors';

  const onboardingKey = useMemo(() => {
    if (!(loading || loadingConnectors) && currentTab === 'chargers')
      return 'charge_center_details';
    if (!(loading || loadingConnectors) && currentTab === 'connectors')
      return 'charge_center_details_connectors';
    return '';
  }, [currentTab, loading, loadingConnectors]);

  useOnboarding(
    onboardingKey,
    { gridConnectionId, dispatch, setSelectedTab, openModal, closeModal },
    () => {
      setSelectedTab(0);
      dispatch(setSelectedConnector(null));
      dispatch(setSelectedChargePoint(null));
      dispatch(removeChargePointByGridConnectionId(Number(gridConnectionId), -1));
      dispatch(removeConnectorById(-1));
      dispatch(removeConnectorById(-2));
      dispatch(removeConnectorById(-3));
      dispatch(removeConnectorById(-4));
      dispatch(removeConnectorById(-5));
      dispatch(removeConnectorByGridConnectionId(Number(gridConnectionId), -1));
      dispatch(removeConnectorByGridConnectionId(Number(gridConnectionId), -2));
      dispatch(removeConnectorByGridConnectionId(Number(gridConnectionId), -3));
      dispatch(removeConnectorByGridConnectionId(Number(gridConnectionId), -4));
      dispatch(removeConnectorByGridConnectionId(Number(gridConnectionId), -5));
      closeModal();
    },
    800
  );

  const handleCloseDrawer = useCallback(async () => {
    await navigate({
      to: route.latestLocation.pathname,
      search: (prev) => ({
        ...prev,
        chargepoint: undefined,
        connector: undefined,
      }),
      replace: true,
    });

    dispatch(unselectConnector());
    dispatch(unselectChargePoint());
  }, [dispatch, navigate, route.latestLocation.pathname]);

  const selectedChargePointConnectors = useMemo(() => {
    if (gridConnectionConnectors && !!selectedChargePoint) {
      const chargePointConnectors = gridConnectionConnectors.filter(
        (connector) => connector.charge_point === selectedChargePoint.id
      );
      return chargePointConnectors || [];
    }
    return [];
  }, [gridConnectionConnectors, selectedChargePoint]);

  const updateGridConnectionPowers = useCallback(async () => {
    if (selectedGridConnection) {
      await dispatch(fetchGridConnectionMetersPower(Number(gridConnectionId)));
      void dispatch(fetchGridConnectionChargersPower(Number(gridConnectionId)));
    }
  }, [dispatch, gridConnectionId, selectedGridConnection]);

  useEffect(() => {
    if (!selectedGridConnection || !Object.keys(selectedGridConnection).length) {
      setLoading(true);
      void dispatch(fetchGridConnection(Number(gridConnectionId))).then(() => {
        setLoading(false);
      });
    }
  }, [dispatch, gridConnectionId, selectedGridConnection]);

  useEffect(() => {
    if (!chargePoints || !Object.keys(chargePoints).length) {
      void dispatch(fetchChargePointsByGridConnectionId(Number(gridConnectionId)));
    }
  }, [dispatch, gridConnectionId, chargePoints]);

  useEffect(() => {
    if (gridConnectionId) {
      setLoadingConnectors(true);
      void dispatch(fetchConnectorsByGridConnectionId(Number(gridConnectionId))).then(() => {
        setLoadingConnectors(false);
      });
    }
  }, [dispatch, gridConnectionId]);

  useEffect(() => {
    if (!selectedConnector && gridConnectionConnectors.length > 0 && connector)
      dispatch(selectConnector(connector));
  }, [dispatch, gridConnectionConnectors, selectedConnector, connector]);

  useEffect(() => {
    if (chargepoint && chargePoints && !selectedChargePoint)
      dispatch(selectChargePoint(chargepoint));
  }, [dispatch, chargePoints, selectedChargePoint, chargepoint]);

  useEffect(() => {
    void updateGridConnectionPowers();
    const interval = setInterval(() => {
      void updateGridConnectionPowers();
    }, 10000);
    return () => clearInterval(interval);
  }, [updateGridConnectionPowers]);

  useEffect(() => {
    if (synclogWebsocket) {
      const newWebsocket = synclogWebsocket;
      if (newWebsocket.readyState === 1 && firstRender.current) {
        const subsMsg = {
          type: 'subscribe',
          topic: 'synclog',
        };
        newWebsocket.send(JSON.stringify(subsMsg));
        firstRender.current = false;
      }
      newWebsocket.onmessage = (event) => {
        const json = JSON.parse(event?.data ? event.data : undefined);
        if (json && json.type === 'authorization' && json.message === false) {
          newWebsocket.close();
        }
        if (
          json?.message.includes?.('/company/grid_connections/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = json.data ? json : JSON.parse(json.message);
          const newGridConnection = messageData?.data;
          if (newGridConnection?.id === gridConnectionId)
            void dispatch(updateGridConnection(newGridConnection));
        }

        if (
          json?.message.includes?.('/chargepoints/charge_points/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = json.data ? json : JSON.parse(json.message);
          const newChargePoint = messageData.data;
          if (newChargePoint.grid_connection === gridConnectionId) {
            void dispatch(updateChargePoint(newChargePoint));
            if (fetchingChargePoints.includes(newChargePoint.id)) {
              dispatch(removeFetchingChargePoint(newChargePoint.id));
              dispatch(
                showSnackbarMessage(
                  `Se actualizaron los conectores del cargador${
                    newChargePoint ? ` ${newChargePoint.verbose_name}` : ''
                  }.`
                )
              );
            }
          }
        }

        if (
          json?.message.includes?.('/chargepoints/connectors/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = json.data ? json : JSON.parse(json.message);
          const newConnector = messageData.data;

          if (newConnector) void dispatch(fetchUpdatedConnector(newConnector));

          if (
            (newConnector?.latest_status_notification?.status === ConnectorStatusEnum.Charging ||
              newConnector?.latest_status_notification?.status === ConnectorStatusEnum.Finishing) &&
            !newConnector?.latest_meter_value?.transaction
          )
            dispatch(addDesyncedChargingConnector(newConnector.id));
          else dispatch(removeDesyncedChargingConnector(newConnector?.id));
        }

        if (
          json?.message.includes?.('/chargepoints/sampled_values/') &&
          (json.message.includes('update') || json.message.includes('created'))
        ) {
          const messageData = json.data ? json : JSON.parse(json.message);
          const newSampledValue = messageData.data;
          const connector = gridConnectionConnectors
            ? gridConnectionConnectors.find(
                (connector) => connector.id === newSampledValue.connector
              )
            : undefined;

          if (!!connector && connector?.latest_transaction)
            dispatch(
              loadSampledValuesByTransactionId(connector.latest_transaction?.id, newSampledValue)
            );
        }
      };
    }

    return () => {
      if (synclogWebsocket) {
        const newWebsocket = synclogWebsocket;
        newWebsocket.onmessage = () => {};
      }
    };
  }, [
    dispatch,
    synclogWebsocket,
    gridConnectionId,
    chargePoints,
    gridConnectionConnectors,
    fetchingChargePoints,
  ]);

  if (loading || loadingConnectors) return <ViewLoading />;

  return (
    <>
      <div className="charge-center-details-container">
        <div className="d-flex justify-content-between align-items-ceenter mb-3">
          <SectionTitle
            className="w-100"
            title={<IntlMessages id="pages.charge-center.grid-connection.subtitle-1" />}
          />
          <OnboardingButton onClick={() => selectedTab === 1 && setSelectedTab(0)} />
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-12 p-0">
              {selectedGridConnection &&
              Object.keys(selectedGridConnection).length &&
              chargePoints ? (
                <GridConnectionChargeMonitor
                  gridConnection={selectedGridConnection}
                  gridConnectionChargePoints={gridConnectionChargePoints}
                  metersPower={metersPower[Number(gridConnectionId)]}
                  chargersPower={chargersPower[Number(gridConnectionId)]}
                  connectors={gridConnectionConnectors}
                />
              ) : null}
            </div>
          </div>
          <div className="gc-detail-tabs row mb-4">
            <div className="section-title-wrapper mt-4 w-100">
              <Tabs value={currentTab}>
                <TabsList className="$tw-bg-muted/50">
                  <TabsTrigger
                    value="chargers"
                    onClick={() =>
                      void navigate({
                        to: '/charge-center/grid-connections/$gridConnectionId/charge-points',
                        params: { gridConnectionId },
                      })
                    }
                  >
                    Por cargador
                  </TabsTrigger>
                  <TabsTrigger
                    value="connectors"
                    onClick={() =>
                      void navigate({
                        to: '/charge-center/grid-connections/$gridConnectionId/connectors',
                        params: { gridConnectionId },
                      })
                    }
                  >
                    Por conector
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="tabs-content w-100 $tw-mt-4">
              <Outlet />
            </div>
          </div>
        </div>

        <Drawer
          anchor="right"
          open={!!selectedConnector || !!selectedChargePoint}
          onClose={handleCloseDrawer}
          PaperProps={{ className: 'charge-center-drawer' }}
        >
          <div className="d-flex items-align-center justify-content-end sticky-top bg-martinique-blue-dark">
            <Button size="icon" variant="ghost" onClick={handleCloseDrawer}>
              <X />
            </Button>
          </div>
          {selectedConnector ? <ConnectorDetails /> : null}
          {selectedChargePoint ? (
            <ChargePointDetails
              chargePoint={selectedChargePoint}
              chargePointConnectors={selectedChargePointConnectors}
            />
          ) : null}
        </Drawer>
      </div>

      <UptimeOnboarding open={isOpen} onClose={closeModal} />
    </>
  );
};

export default Details;
